// ---------------------------------------------------------------------------
// Validación y ajuste de medios ANTES de subirlos.
//
// Las pantallas (CallScreen / CallScreenV2) escalan el medio a pantalla completa
// con object-fit: cover, así que un archivo de baja resolución se ve pixelado al
// agrandarse. Por eso:
//   1. Se exige una resolución mínima (full HD recomendado).
//   2. Las imágenes demasiado grandes se reescalan/comprimen antes de subir.
//   3. Los videos solo se validan (no se pueden reescalar en el navegador).
// ---------------------------------------------------------------------------

// Resolución mínima aceptada. Un medio es válido si su LADO CORTO es >= 720 y su
// LADO LARGO es >= 1280 (sirve para pantallas horizontales o verticales).
export const MIN_LADO_CORTO = 720;
export const MIN_LADO_LARGO = 1280;

// Resolución ideal de referencia (kioscos Full HD).
export const ANCHO_RECOMENDADO = 1920;
export const ALTO_RECOMENDADO = 1080;

// Lado máximo al que se reescalan las imágenes (no se suben archivos enormes).
export const MAX_LADO_IMAGEN = 1920;
// Peso máximo (MB) antes de recomprimir una imagen.
export const PESO_MAX_MB_IMAGEN = 3;

export const TEXTO_REQUISITO = `Mínimo ${MIN_LADO_CORTO}×${MIN_LADO_LARGO} px · recomendado ${ANCHO_RECOMENDADO}×${ALTO_RECOMENDADO} px (Full HD)`;

export const dimensionesValidas = (width, height) =>
    width > 0 && height > 0 &&
    Math.min(width, height) >= MIN_LADO_CORTO &&
    Math.max(width, height) >= MIN_LADO_LARGO;

const cargarImagen = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo leer la imagen'));
    img.src = src;
});

export const leerDimensionesImagen = async (file) => {
    const objUrl = URL.createObjectURL(file);
    try {
        const img = await cargarImagen(objUrl);
        return { width: img.naturalWidth, height: img.naturalHeight, img };
    } finally {
        URL.revokeObjectURL(objUrl);
    }
};

export const leerDimensionesVideo = (file) => new Promise((resolve, reject) => {
    const objUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const limpiar = () => URL.revokeObjectURL(objUrl);

    video.onloadedmetadata = () => {
        const dims = { width: video.videoWidth, height: video.videoHeight };
        limpiar();
        resolve(dims);
    };
    video.onerror = () => {
        limpiar();
        reject(new Error('No se pudo leer el video'));
    };

    video.src = objUrl;
});

const reescalarImagen = (img, width, height, maxLado) => {
    const ladoMayor = Math.max(width, height);
    const escala = Math.min(1, maxLado / ladoMayor);
    const w = Math.round(width * escala);
    const h = Math.round(height * escala);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (blob) return resolve(blob);
            // Respaldo si el navegador no soporta WebP
            canvas.toBlob((jpeg) => resolve(jpeg), 'image/jpeg', 0.92);
        }, 'image/webp', 0.92);
    });
};

// Analiza y (si hace falta) ajusta una imagen antes de subirla.
// Devuelve { file, width, height, valida, optimizada, pesoFinalKB }.
export const prepararImagen = async (file) => {
    const tipo = file.type;

    // Los SVG son vectoriales: escalan sin pixelarse y no se tocan.
    if (tipo === 'image/svg+xml') {
        return { file, width: null, height: null, valida: true, optimizada: false };
    }

    const { width, height, img } = await leerDimensionesImagen(file);
    const valida = dimensionesValidas(width, height);

    if (!valida) {
        return { file, width, height, valida, optimizada: false };
    }

    // Los GIF pueden ser animados: se validan pero no se recomprimen.
    const ladoMayor = Math.max(width, height);
    const pesoMB = file.size / (1024 * 1024);
    const necesitaAjuste = ladoMayor > MAX_LADO_IMAGEN || pesoMB > PESO_MAX_MB_IMAGEN;

    if (tipo === 'image/gif' || !necesitaAjuste) {
        return { file, width, height, valida, optimizada: false };
    }

    try {
        const blob = await reescalarImagen(img, width, height, MAX_LADO_IMAGEN);
        if (!blob) {
            return { file, width, height, valida, optimizada: false };
        }

        const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
        const base = (file.name || 'imagen').replace(/\.[^.]+$/, '');
        const archivoOptimizado = new File([blob], `${base}.${ext}`, { type: blob.type });

        return {
            file: archivoOptimizado,
            width,
            height,
            valida,
            optimizada: true,
            pesoFinalKB: Math.round(blob.size / 1024),
        };
    } catch (e) {
        console.warn('No se pudo optimizar la imagen, se sube original:', e);
        return { file, width, height, valida, optimizada: false };
    }
};
